import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MessageSquare, Send, Circle, User, Phone } from "lucide-react";

export default function SmsInboxPage() {
  const { propertyId } = useProperty();
  const [threads, setThreads] = useState([]);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const bodyRef = useRef(null);

  const loadThreads = async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ property_id: propertyId });
      if (unreadOnly) params.set("unread_only", "true");
      const { data } = await api.get(`/admin/sms/threads?${params}`);
      setThreads(data.items || []);
      setStats(data.stats || null);
    } catch (e) { toast.error("Failed to load threads"); }
    setLoading(false);
  };

  const openThread = async (t) => {
    setSelected(t);
    try {
      const { data } = await api.get(`/admin/sms/threads/${t.id}`);
      setMessages(data.messages || []);
      setTimeout(() => bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight }), 50);
      // Refresh list to clear unread count
      loadThreads();
    } catch (e) { toast.error("Failed to load thread"); }
  };

  const sendReply = async () => {
    if (!selected || !reply.trim()) return;
    try {
      await api.post(`/admin/sms/threads/${selected.id}/reply`, { body: reply });
      setReply("");
      openThread(selected);
      toast.success("Reply queued");
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };

  useEffect(() => {
    loadThreads();
    const iv = setInterval(loadThreads, 15000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, unreadOnly]);

  return (
    <div className="space-y-6" data-testid="sms-inbox-page">
      <PageHeader
        eyebrow="Communications"
        title="Two-Way SMS Inbox"
        subtitle="Every inbound reply from residents, threaded per contact. Reply directly from the app."
        actions={
          <div className="flex items-center gap-3">
            {stats && (
              <div className="text-xs text-muted-foreground">
                <span data-testid="stat-total">{stats.total_threads}</span> threads ·
                <span className="text-amber-500 ml-1" data-testid="stat-unread">{stats.unread_threads}</span> unread
              </div>
            )}
            <Button size="sm" variant={unreadOnly ? "default" : "outline"} onClick={() => setUnreadOnly(!unreadOnly)} data-testid="toggle-unread">
              {unreadOnly ? "Show all" : "Unread only"}
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-12 gap-4 min-h-[60vh]">
        {/* Thread list */}
        <Card className="col-span-4 border-white/10 bg-card overflow-hidden">
          <div className="p-3 text-xs uppercase tracking-wider text-muted-foreground border-b border-white/10">
            {threads.length} thread{threads.length === 1 ? "" : "s"}
          </div>
          <div className="divide-y divide-white/5 max-h-[70vh] overflow-y-auto">
            {threads.length === 0 && !loading && (
              <div className="text-center py-8 text-muted-foreground text-sm" data-testid="threads-empty">
                No messages yet. Inbound replies from residents will appear here.
              </div>
            )}
            {threads.map((t) => (
              <button
                key={t.id}
                onClick={() => openThread(t)}
                className={`w-full text-left p-3 hover:bg-white/5 transition ${selected?.id === t.id ? "bg-white/5" : ""}`}
                data-testid={`thread-${t.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {t.unread_count > 0 && (
                        <Circle size={8} className="fill-amber-500 text-amber-500 flex-shrink-0" />
                      )}
                      <div className="font-medium truncate">
                        {t.resident_name || t.phone || "Unknown"}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {t.last_direction === "outbound" && <span className="mr-1">You:</span>}
                      {t.last_message}
                    </div>
                    {t.unit && <div className="text-[10px] text-muted-foreground mt-1">Unit {t.unit}</div>}
                  </div>
                  <div className="text-[10px] text-muted-foreground flex-shrink-0">
                    {(t.last_message_at || "").slice(11, 16)}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </Card>

        {/* Conversation view */}
        <Card className="col-span-8 border-white/10 bg-card overflow-hidden flex flex-col" data-testid="conversation-panel">
          {selected ? (
            <>
              <div className="p-3 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <User size={16} className="text-muted-foreground" />
                  <div>
                    <div className="font-medium">{selected.resident_name || "Unknown"}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <Phone size={10} /> {selected.phone}
                      {selected.unit && <span className="ml-2">Unit {selected.unit}</span>}
                    </div>
                  </div>
                </div>
                {!selected.user_id && (
                  <Badge variant="outline" className="text-amber-500 border-amber-500/40">Unmapped contact</Badge>
                )}
              </div>
              <div ref={bodyRef} className="flex-1 p-4 space-y-3 overflow-y-auto max-h-[55vh]">
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                    <div className={`max-w-[70%] px-3 py-2 rounded-2xl text-sm ${m.direction === "outbound" ? "bg-emerald-600 text-white" : "bg-white/10 text-foreground"}`}>
                      <div>{m.body}</div>
                      <div className={`text-[10px] mt-1 ${m.direction === "outbound" ? "text-emerald-100" : "text-muted-foreground"}`}>
                        {(m.sent_at || "").slice(11, 16)}
                        {m.direction === "outbound" && <span className="ml-1">· {m.status}</span>}
                      </div>
                    </div>
                  </div>
                ))}
                {messages.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground text-sm">No messages yet.</div>
                )}
              </div>
              <div className="p-3 border-t border-white/10 space-y-2">
                <Textarea
                  rows={2}
                  placeholder="Type a reply…"
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && e.metaKey) sendReply(); }}
                  data-testid="reply-input"
                />
                <div className="flex justify-between items-center">
                  <div className="text-xs text-muted-foreground">{reply.length}/1600 · ⌘+Enter to send</div>
                  <Button size="sm" onClick={sendReply} disabled={!reply.trim()} className="gap-2" data-testid="send-reply">
                    <Send size={12} /> Send
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
              <div className="text-center">
                <MessageSquare size={28} className="mx-auto opacity-40 mb-2" />
                <div>Pick a thread on the left to view the conversation</div>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
